import { useMatomo } from '@datapunt/matomo-tracker-react';
import cx from 'clsx';
import { format } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { ReactComponent as CopyIcon } from '@assets/svg/ic-copyDetails.svg';

import Header from '@components/common/Header/Header';
import Loader from '@components/common/Loader/Loader';
import StatusTypeItem from '@components/common/StatusTypeItem/StatusTypeItem';
import TooltipStatus from '@components/common/StatusTypeItem/TooltipStatus';
import TableEmptyRow from '@components/common/TableEmptyRow/TableEmptyRow';
import Tabs from '@components/uikit/Tabs/Tabs';

import { calcSensorHex } from '@helpers/helper';
import { showToast } from '@helpers/showToast';

import useTranslate from '@hooks/useTranslate';
import useTranslation from '@hooks/useTranslation';

import { useActionItemFullQuery } from '@services/actionItem/useActionItemFullQuery';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import AiHistoryExtraInfo from './components/AiHistoryExtraInfo/AiHistoryExtraInfo';
import { getAIActionsById } from './helpers/getAIActionsById';
import { getEquipmentName } from './helpers/getEquipmentName';
import { useHistoryByAIQuery } from './services/useHistoryByAIQuery';
import { useListByEquipmentQuery } from './services/useListByEquipmentQuery';
import style from './style.module.scss';

const AiHistory = () => {
  const { trackPageView } = useMatomo();
  const { t } = useTranslation();
  const { translateField } = useTranslate();

  const navigate = useNavigate();
  const { customerId, actionItemId } = useParams();
  const { pathname } = useLocation();
  const [pageType, setPageType] = useState('instPoint');

  const { data: profileData } = useUserProfileQuery();
  const { data: actionItemData } = useActionItemFullQuery({ actionItemId: actionItemId || '' });

  const actionItem = actionItemData?.actionItem;
  const history = actionItemData?.history || [];
  const dateTimeFormat = `${profileData?.dateFormat.date}, ${profileData?.dateFormat.time}`;
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const historyById = getAIActionsById(history);

  const installationPoint = actionItem?.installation_point || {};
  const sensor = actionItem?.installation_point?.sensor || {};
  const equipment = installationPoint?.equipment || {};
  const equipmentType = actionItem?.equipment_type;
  const equipmentName = getEquipmentName(equipment, equipmentType, t);
  const isMote = equipmentType === 'mote';
  const isSensor = equipmentType === 'sensor';
  const calcSensor =
    isMote || isSensor
      ? calcSensorHex(sensor?.hardware_id, actionItem?.sensor_id)
      : actionItem?.serial || '---';
  const showHistoryTabs =
    equipmentType !== 'node' && equipmentType !== 'gateway' && equipmentType !== 'router';

  const { isInitialLoading: isInitialLoadingHistoryByAI, data: historyByAIData } = useHistoryByAIQuery({
    actionItemId,
    equipmentId: equipment?.id,
  });

  const { isInitialLoading: isInitialLoadingListByEquipment, data: listByEquipmentData } =
    useListByEquipmentQuery({
      equipmentId: equipment?.id,
      installationPointId: installationPoint?.id,
      isByEquipment: pageType === 'equipment',
    });

  const tableData = listByEquipmentData || historyByAIData || [];

  useEffect(() => {
    trackPageView();
  }, []);

  const copy = () => {
    try {
      navigator.clipboard.writeText(calcSensor).then(() => {
        showToast({ variant: 'success', message: t('Successfully copied to clipboard') });
      });
    } catch (e) {
      console.log(e);
    }
  };

  if (isInitialLoadingHistoryByAI || isInitialLoadingListByEquipment) {
    return <Loader />;
  }

  return (
    <div>
      <Header
        title={t('Action item full history')}
        isSecondaryPage={true}
        extraInfo={
          <AiHistoryExtraInfo
            info={`${actionItem?.facility?.name || '-'} / ${actionItem?.location?.name || '-'}`}
          />
        }
      />
      <div className={style.historyPageBody}>
        <div className={style.historyPageBodyContainer}>
          <div className={style.historyPageInfoPanel}>
            <div className={style.leftPart}>
              {showHistoryTabs && (
                <Tabs
                  id="ai-history"
                  value={pageType}
                  tabsItems={[
                    { value: 'instPoint', label: t('Installation point') },
                    { value: 'equipment', label: t('Equipment') },
                  ]}
                  label={t('History by')}
                  type={'woHelperText'}
                  onChange={setPageType}
                />
              )}
              <div>
                <div className={style.equipmentNameLabel}>{t('Equipment name')}</div>
                <div className={style.equipmentName}>{equipmentName}</div>
              </div>
            </div>
            <div className={style.serialNumberBlock}>
              <div className={style.serialNumberLabel}>
                {t(isMote || isSensor ? 'Hex Code' : 'Serial Number')}
              </div>
              <div className={style.serialNumberField}>
                <div className={style.serialNumber}>{calcSensor}</div>
                <CopyIcon className={style.copyIcon} onClick={() => copy()} />
              </div>
            </div>
          </div>
          <table className={style.historyTable} cellSpacing="0" cellPadding="0">
            <thead className={style.historyTableHead}>
              <tr>
                <th className={style.historyTableHeadCell}>{t('Action Item')}</th>
                <th className={style.historyTableHeadCell}>{t('Installation point')}</th>
                <th className={style.historyTableHeadCell}>{t('Status')}</th>
                <th className={style.historyTableHeadCell}>{t('Created')}</th>
                <th className={style.historyTableHeadCell}>{t('Archived')}</th>
                <th className={style.historyTableHeadCell}>{t('Actions')}</th>
              </tr>
            </thead>
            <tbody className={style.historyTableBody}>
              {tableData.length ? (
                tableData.map((row) => (
                  <tr
                    key={row.id}
                    className={style.historyTableBodyRow}
                    onClick={() => {
                      navigate(`/customer/${customerId}/ai/${row.id}`, { state: { prevPage: pathname } });
                    }}
                  >
                    <td className={style.historyTableBodyCell}>{row.title}</td>
                    <td className={style.historyTableBodyCell}>
                      {translateField({ item: row.installation_point })}
                    </td>
                    <td className={style.historyTableBodyCell}>
                      <StatusTypeItem item={row} hideTroubleshooting={true} tooltipId="status-tooltip" />
                    </td>
                    <td className={style.historyTableBodyCell}>
                      {format(fromZonedTime(row.time_created, userTimezone), dateTimeFormat)}
                    </td>
                    <td className={cx(style.historyTableBodyCell, style.uppercaseCell)}>
                      {row.closed_at && format(fromZonedTime(row.closed_at, userTimezone), dateTimeFormat)}
                    </td>
                    <td className={style.historyTableBodyCell}>
                      <div
                        className={style.actionsInfo}
                        dangerouslySetInnerHTML={{ __html: historyById[row.id] }}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <TableEmptyRow text={t('No action items were found')} />
              )}
              <TooltipStatus id="status-tooltip" />
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AiHistory;
